import React, { forwardRef, useId, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
} from '@mui/material';

import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import EuroIcon from '@mui/icons-material/Euro';

import Tooltip from '@mui/material/Tooltip';

export interface TextInputProps {
  label?: string | null;
  type?: string;
  error?: boolean;
  helperText?: string | null;
  /* eslint-disable-next-line */
  endAdornment?: any;
  /* eslint-disable-next-line */
  value?: any;
  tooltipText?: string | null;
  multiline?: boolean;
  placeholder?: string;
  /* eslint-disable-next-line */
  onChange?: any;
  /* eslint-disable-next-line */
  onBlur?: any;
  disabled?: boolean;
  /* eslint-disable-next-line */
  InputProps?: any;
  /* eslint-disable-next-line */
  inputProps?: any;
  minRows?: number | string;
  showEuroIcon?: boolean;
}

export const CustomTextInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
  },
  '& .MuiInputBase-input': {
    borderRadius: '8px',
    position: 'relative',
    backgroundColor: 'white',
    border: '1px solid #5E6366',
    fontSize: 16,
    width: '100%',
    padding: '10px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    // Use the system font instead of the default Roboto font.
    '&:focus': {
      boxShadow: `${alpha('#8B8D97', 0.25)} 0 0 0 0.2rem`,
      borderColor: '#8B8D97',
      borderRadius: '8px',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'all !important',
    },
  },
}));

const TextInput = forwardRef(
  (
    {
      label = null,
      type = 'text',
      error,
      helperText,
      endAdornment = null,
      tooltipText = null,
      multiline = false,
      minRows,
      showEuroIcon = false,
      ...other
    }: TextInputProps,
    ref
  ) => {
    // workaround for InputProps Error
    delete other?.InputProps;
    const [showPass, setShowPass] = useState(false);
    const inputId = useId();

    return (
      <FormControl
        variant="standard"
        fullWidth
        error={error}
        sx={{ margin: '12px 0', position: 'relative' }}
      >
        {label && (
          <InputLabel
            shrink
            htmlFor={inputId}
            sx={{
              color: '#5E6366',
              pl: 1,
              '&.Mui-focused': {
                color: error ? '#F9473C' : '#5E6366 !important',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <Tooltip
          title={tooltipText}
          placement="bottom-start"
          sx={{ zIndex: 99 }}
        >
          <CustomTextInput
            id={inputId}
            type={showPass ? 'text' : type}
            sx={[
              helperText
                ? {
                    boxShadow: (theme) =>
                      `${alpha(theme.palette.error.main, 0.5)} 0 0 0 0.2rem`,
                    borderColor: (theme) => theme.palette.error.main,
                    borderRadius: '8px',
                  }
                : {},
              type === 'password' || showEuroIcon
                ? {
                    '& .MuiInputBase-input': { pr: '35px' },
                  }
                : {},
            ]}
            multiline={multiline}
            minRows={minRows}
            inputProps={
              multiline
                ? {
                    style: {
                      marginTop: -5,
                      marginBottom: -5,
                    },
                  }
                : {}
            }
            {...other}
            ref={ref}
          />
        </Tooltip>

        {showEuroIcon && (
          <InputAdornment
            position="end"
            sx={{
              position: 'absolute',
              right: 10,
              bottom: helperText ? 44 : 22,
            }}
          >
            <EuroIcon />
          </InputAdornment>
        )}

        {type === 'password' && (
          <InputAdornment
            position="end"
            sx={{
              position: 'absolute',
              right: 10,
              bottom: helperText ? 44 : 22,
            }}
          >
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPass((val) => !val)}
              edge="end"
            >
              {showPass ? (
                <VisibilityOffOutlinedIcon />
              ) : (
                <VisibilityOutlinedIcon />
              )}
            </IconButton>
          </InputAdornment>
        )}
        {helperText && (
          <FormHelperText sx={{ pl: 1 }}>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }
);

export default TextInput;
